import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCustomer } from '../contexts/CustomerContext';
import useOfferCoupon from './useOfferCoupon';
import { Cart } from '../models';
import QUERY_KEYS from '../utils/QueryKeys';
import { offerRemovedKey } from './useApplyOfferToCart';
import { offerSkip, lastPackageIdOf, offerSettled } from './offerApplyState';

/**
 * Ensure the customer's promotional offer is applied to their cart by the time
 * the checkout summary first paints — resolving EVERYTHING from the server, not
 * localStorage context.
 *
 * Why server truth matters (2026-07-22 incident, round two): the review screen
 * originally scoped the offer from `customer.cart.orderPackages` in context,
 * which is only populated for STALE sessions restored from localStorage. A
 * fresh session (employee test, cleared browser) had an empty context array, so
 * the resolver never engaged and checkout showed no discount — while stale
 * sessions looked fine. The cart's order packages are fetched from the API here.
 *
 * Guards (same contract as useApplyOfferToCart, which remains for the pricelist
 * surfaces): never overwrite an existing coupon; honor an explicit removal
 * (localStorage marker); once per (cart, offer); a failed save settles honestly
 * and clears the retry guard. Priority-cart flows skip entirely.
 *
 * Returns { settled, offer }: gate the summary's first paint on `settled` and
 * pair it with a timeout failsafe so a slow query can never hold the page.
 */
export default function useEnsureOfferApplied() {
  const { customer, setCustomer } = useCustomer();
  const cartId = customer?.cart?.id;
  const skip = offerSkip({
    priorityCart: customer?.priorityCart,
    cartId,
    studentId: customer?.currentStudent?.studentId,
    schoolId: customer?.currentSchool?.id,
  });

  // Server cart WITH its order packages — the offer's pricelist scope comes from
  // the latest package the customer actually put in the cart. Fetched via the
  // show endpoint (Cart.find): the index endpoint requires filter[for_email]
  // for non-admins and 404s a filter[id] lookup, which left `cart` undefined
  // for real customers and the offer never applied (2026-07-27).
  const { data: cart, isFetched: cartFetched } = useQuery(
    [QUERY_KEYS.CART, cartId, 'withOrderPackages'],
    () => Cart.includes('order_packages').find(cartId).then((res) => res.data),
    { enabled: !skip },
  );

  const lastPackageId = lastPackageIdOf(cart);

  const { data: offer, isFetched: offerFetched } = useOfferCoupon(
    // No package in the cart = no pricelist context = resolve nothing (an
    // unscoped resolution could hand a senior-only promo to an undergrad).
    !skip && lastPackageId ? customer?.currentStudent?.studentId : undefined,
    customer?.currentSchool?.id,
    lastPackageId,
  );

  const appliedRef = useRef(null);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (skip || !offer || !cart) return;
    if (cart.couponId) {
      setApplied(true);
      return;
    }
    if (localStorage.getItem(offerRemovedKey(cart.id))) {
      setApplied(true);
      return;
    }
    const applyKey = `${cart.id}:${offer.id}`;
    if (appliedRef.current === applyKey) return;

    appliedRef.current = applyKey;
    cart.couponId = offer.id;
    cart.save()
      .then(() => {
        setApplied(true);
        setCustomer((prev) => ({ ...prev, cart }));
      })
      .catch((err) => {
        console.error('useEnsureOfferApplied: apply failed', err);
        appliedRef.current = null;
        cart.couponId = null;
        setApplied(true);
      });
  }, [skip, offer, cart, setCustomer]);

  const settled = offerSettled({
    skip, cartFetched, lastPackageId, offerFetched, offer, applied,
  });

  return { settled, offer };
}
